/*! Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved. */

import { ILatLng } from '../../core/models';
import { IRoadDetails, ISegmentGroup, ISelectedSegment, ROAD_DIRECTION } from '../models';

/** Spatial tolerance in degrees (~15 meters) for endpoint adjacency checks. */
export const TOLERANCE = 0.00015;

/** Number of points to check near each segment endpoint for adjacency. */
const ENDPOINT_DEPTH = 3;

/**
 * Determines whether two coordinates are within the given tolerance.
 *
 * @param p1 - First coordinate point
 * @param p2 - Second coordinate point
 * @param tolerance - Maximum allowed difference on each axis
 * @returns `true` if both lat and lng differences are below tolerance
 */
export function isWithinTolerance(p1: ILatLng, p2: ILatLng, tolerance: number): boolean {
  return Math.abs(p1.lat - p2.lat) < tolerance && Math.abs(p1.lng - p2.lng) < tolerance;
}

/**
 * Checks whether two segments share an endpoint within the given tolerance.
 * Compares the endpoint regions (first/last N points) of both segments
 * to handle cases where the HERE API geometry doesn't place the shared
 * junction point at the exact first or last index.
 */
export function areEndpointsAdjacent(segA: ISelectedSegment, segB: ISelectedSegment, tolerance: number): boolean {
  const regA = getEndpointRegions(segA.geometry, ENDPOINT_DEPTH);
  const regB = getEndpointRegions(segB.geometry, ENDPOINT_DEPTH);

  const pairs: [ILatLng[], ILatLng[]][] = [
    [regA.end, regB.start],
    [regA.start, regB.end],
    [regA.end, regB.end],
    [regA.start, regB.start]
  ];

  for (const [regionX, regionY] of pairs) {
    for (const px of regionX) {
      for (const py of regionY) {
        if (isWithinTolerance(px, py, tolerance)) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Computes merged road details for a group of contiguous segments.
 */
export function buildMergedRoadDetails(segments: ISelectedSegment[]): IRoadDetails {
  const fullAddress = segments[0].roadName;
  const roadName = segments[0].roadDetails.roadName;
  const cityName = segments[0].roadDetails.cityName;
  const speeds = new Set(segments.map(s => s.roadDetails.speedLimitInKmh).filter((s): s is number => s != null));

  let speedDisplay: string | undefined;
  if (speeds.size > 1) {
    const sorted = [...speeds].sort((a, b) => a - b);
    speedDisplay = `${sorted[0]} - ${sorted[sorted.length - 1]}`;
  }

  const totalDistanceKm = segments.reduce((sum, s) => sum + (s.roadDetails.distanceKm ?? 0), 0);
  const directions = new Set(segments.map(s => s.roadDetails.direction));
  const direction = directions.size === 1 ? [...directions][0] : ROAD_DIRECTION.Both;

  return {
    fullAddress,
    roadName,
    cityName,
    speedDisplay,
    direction,
    distanceKm: totalDistanceKm,
    speedLimitInKmh: speeds.size === 1 ? [...speeds][0] : null
  };
}

/**
 * Concatenates segment geometries in spatial order, removing duplicate
 * boundary points. Handles segments that may need to be reversed.
 * For branching topologies, backtracks along the already-merged path
 * to the nearest connection point when the next segment is not adjacent
 * to the current chain tail.
 */
export function mergeGroupGeometry(segments: ISelectedSegment[]): ILatLng[] {
  if (segments.length === 0) return [];
  if (segments.length === 1) return [...segments[0].geometry];

  const first = segments[0].geometry;
  const second = segments[1].geometry;

  const endToSecond = Math.min(
    distanceSq(first[first.length - 1], second[0]),
    distanceSq(first[first.length - 1], second[second.length - 1])
  );
  const startToSecond = Math.min(distanceSq(first[0], second[0]), distanceSq(first[0], second[second.length - 1]));

  let merged: ILatLng[] = startToSecond < endToSecond ? [...first].reverse() : [...first];

  for (let i = 1; i < segments.length; i++) {
    const segGeometry = segments[i].geometry;
    const lastMerged = merged[merged.length - 1];

    // Check if the segment connects to the current tail
    const connectsToTail =
      isWithinTolerance(lastMerged, segGeometry[0], TOLERANCE) ||
      isWithinTolerance(lastMerged, segGeometry[segGeometry.length - 1], TOLERANCE);

    if (!connectsToTail) {
      // Branching: retrace the merged path back to the connection point
      const backtrackPath = buildBacktrackPath(merged, segGeometry);
      for (const point of backtrackPath) {
        merged.push(point);
      }
    }

    const lastPoint = merged[merged.length - 1];
    const dToStart = distanceSq(lastPoint, segGeometry[0]);
    const dToEnd = distanceSq(lastPoint, segGeometry[segGeometry.length - 1]);
    const oriented = dToEnd < dToStart ? [...segGeometry].reverse() : segGeometry;

    const startIndex = isWithinTolerance(merged[merged.length - 1], oriented[0], TOLERANCE) ? 1 : 0;
    for (let j = startIndex; j < oriented.length; j++) {
      merged.push(oriented[j]);
    }
  }

  return merged;
}

/**
 * Builds a backtrack path from the current tail of the merged geometry
 * back to the point closest to the next segment's endpoints.
 * Retraces the merged path in reverse, producing a faithful "walk back"
 * along the already-drawn polyline rather than a straight-line jump.
 */
function buildBacktrackPath(merged: ILatLng[], segGeometry: ILatLng[]): ILatLng[] {
  const segStart = segGeometry[0];
  const segEnd = segGeometry[segGeometry.length - 1];

  // Find the index in merged that is closest to either endpoint of the next segment
  let bestIndex = merged.length - 1;
  let bestDist = Infinity;

  for (let idx = 0; idx < merged.length; idx++) {
    const dStart = distanceSq(merged[idx], segStart);
    const dEnd = distanceSq(merged[idx], segEnd);
    const d = Math.min(dStart, dEnd);
    if (d < bestDist) {
      bestDist = d;
      bestIndex = idx;
    }
  }

  // Walk backwards from the tail to the connection point
  const backtrack: ILatLng[] = [];
  for (let idx = merged.length - 2; idx >= bestIndex; idx--) {
    backtrack.push(merged[idx]);
  }

  return backtrack;
}

/**
 * Evaluates contiguity across all selected segments and produces an array
 * of maximally contiguous segment groups.
 *
 * Algorithm:
 * 1. Build an adjacency graph between all segments.
 * 2. Find connected components via BFS.
 * 3. Order segments within each component by spatial chain order.
 * 4. Build each group with a deterministic groupId, merged road details, and geometry.
 *
 * @param segments - Record of selected segments keyed by segmentId
 * @returns Array of segment groups, each representing a contiguous chain
 */
export function evaluateContiguity(segments: Record<string, ISelectedSegment>): ISegmentGroup[] {
  const all = Object.values(segments);
  if (all.length === 0) return [];

  const adjacency = buildAdjacencyMap(all);
  const components = findConnectedComponents(all, adjacency);

  return components.map(component => {
    const ordered = orderComponentChain(component, adjacency);
    const groupId = ordered
      .map(s => s.segmentId)
      .sort((a, b) => a.localeCompare(b))
      .join('|');
    return {
      groupId,
      segments: ordered,
      mergedRoadDetails: buildMergedRoadDetails(ordered),
      mergedGeometry: mergeGroupGeometry(ordered)
    };
  });
}

/**
 * Normalizes a road name for comparison.
 */
export function normalizeRoadName(name: string): string {
  return name.trim().toLowerCase();
}

// ── Private helpers ──

function getEndpointRegions(geometry: ILatLng[], depth: number): { start: ILatLng[]; end: ILatLng[] } {
  const n = Math.min(depth, geometry.length);
  return { start: geometry.slice(0, n), end: geometry.slice(-n) };
}

function distanceSq(a: ILatLng, b: ILatLng): number {
  const dlat = a.lat - b.lat;
  const dlng = a.lng - b.lng;
  return dlat * dlat + dlng * dlng;
}

/**
 * Builds an adjacency map for all segments by checking each pair for shared endpoints
 * and matching road names. Segments are only considered adjacent if they are spatially
 * connected AND belong to the same road.
 * Computed once and reused by both component detection and chain ordering.
 */
function buildAdjacencyMap(segments: ISelectedSegment[]): Map<ISelectedSegment, ISelectedSegment[]> {
  const adjacency = new Map<ISelectedSegment, ISelectedSegment[]>();
  for (const seg of segments) {
    adjacency.set(seg, []);
  }
  for (let i = 0; i < segments.length; i++) {
    for (let j = i + 1; j < segments.length; j++) {
      const sameRoad = normalizeRoadName(segments[i].roadName) === normalizeRoadName(segments[j].roadName);
      if (sameRoad && areEndpointsAdjacent(segments[i], segments[j], TOLERANCE)) {
        adjacency.get(segments[i]).push(segments[j]);
        adjacency.get(segments[j]).push(segments[i]);
      }
    }
  }
  return adjacency;
}

/**
 * Finds connected components of segments using BFS over a precomputed adjacency map.
 * Two segments are in the same component if they share an endpoint
 * (directly or transitively through other segments).
 */
function findConnectedComponents(segments: ISelectedSegment[], adjacency: Map<ISelectedSegment, ISelectedSegment[]>): ISelectedSegment[][] {
  const visited = new Set<ISelectedSegment>();
  const components: ISelectedSegment[][] = [];

  for (const seg of segments) {
    if (visited.has(seg)) continue;
    const component: ISelectedSegment[] = [];
    const queue: ISelectedSegment[] = [seg];
    visited.add(seg);
    while (queue.length > 0) {
      const current = queue.shift();
      component.push(current);
      for (const neighbor of adjacency.get(current) ?? []) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      }
    }
    components.push(component);
  }

  return components;
}

/**
 * Orders segments within a connected component using DFS traversal.
 * Starts from a leaf node (degree <= 1) and visits all branches depth-first.
 * For linear chains this produces the natural spatial order.
 * For branching topologies (Y-shapes), segments on side branches are visited
 * immediately after the junction segment, ensuring mergeGroupGeometry can
 * concatenate them without spatial jumps across the map.
 */
function orderComponentChain(component: ISelectedSegment[], adjacency: Map<ISelectedSegment, ISelectedSegment[]>): ISelectedSegment[] {
  if (component.length <= 1) return component;

  // Find the best start: a leaf node (degree <= 1).
  // For a linear chain this is one of the two ends.
  // For a Y-shape this avoids starting at the junction.
  let start = component[0];
  for (const seg of component) {
    if ((adjacency.get(seg)?.length ?? 0) <= 1) {
      start = seg;
      break;
    }
  }

  // DFS traversal — visits every segment exactly once, following branches as they appear
  const ordered: ISelectedSegment[] = [];
  const visited = new Set<ISelectedSegment>();
  const stack: ISelectedSegment[] = [start];

  while (stack.length > 0) {
    const current = stack.pop();
    if (visited.has(current)) continue;
    visited.add(current);
    ordered.push(current);

    // Push neighbors in reverse so the first neighbor is processed next (preserves spatial continuity)
    const neighbors = adjacency.get(current) ?? [];
    for (let i = neighbors.length - 1; i >= 0; i--) {
      if (!visited.has(neighbors[i])) {
        stack.push(neighbors[i]);
      }
    }
  }

  return ordered;
}
